import { formatDistanceToNow } from 'date-fns';
import { Loader2, RefreshCw } from 'lucide-react';
import { useCallback, useMemo, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import {
  useGenerateWikiMutation,
  useGetWikiQuery,
} from '@/services/api/matterWikiService';
import type {
  MatterWikiResponse,
  WikiCitation,
} from '@/services/types/matter-wiki-types';

import { getCitationKey } from './utils/citations';
import { WikiCitationDrawer } from './WikiCitationDrawer';
import { WikiEmptyState } from './WikiEmptyState';
import { WikiGenerationBanner } from './WikiGenerationBanner';
import {
  WikiScrollView,
  type WikiScrollViewHandle,
} from './WikiScrollView';
import { WikiSearch } from './WikiSearch';
import { WikiSidebar } from './WikiSidebar';

const POLL_INTERVAL_MS = 3000;

interface MatterWikiProps {
  matterId: string;
}

const WikiLoadingSkeleton = () => {
  return (
    <div className="flex h-full bg-zinc-50/50">
      <div className="w-[240px] shrink-0 border-r border-zinc-100 p-4">
        <Skeleton className="mb-6 h-5 w-24" />
        <div className="flex flex-col gap-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton
              key={i}
              className="h-8 w-full rounded"
              style={{ opacity: 1 - i * 0.12 }}
            />
          ))}
        </div>
      </div>

      <div className="flex-1 p-8">
        <Skeleton className="mb-3 h-8 w-64" />
        <Skeleton className="mb-6 h-4 w-40" />
        <div className="flex flex-col gap-3">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-11/12" />
          <Skeleton className="h-4 w-4/5" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
        </div>
        <Skeleton className="mt-8 h-4 w-5/6" />
        <div className="mt-3 flex flex-col gap-3">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-11/12" />
          <Skeleton className="h-4 w-2/3" />
        </div>
      </div>
    </div>
  );
};

const WikiHeader = ({
  wiki,
  isRegenerating,
  onRegenerate,
  onSelectPage,
}: {
  wiki: MatterWikiResponse;
  isRegenerating: boolean;
  onRegenerate: () => void;
  onSelectPage: (path: string) => void;
}) => {
  const lastGeneratedLabel = wiki.last_generated_at
    ? formatDistanceToNow(new Date(wiki.last_generated_at), {
        addSuffix: true,
      })
    : null;

  return (
    <div className="grid grid-cols-[1fr_2fr_1fr] items-center gap-4 border-b border-zinc-100 px-6 py-3">
      <div className="flex items-baseline gap-3">
        <h1 className="text-base font-semibold tracking-tight text-zinc-900">
          Matter Wiki
        </h1>
        {lastGeneratedLabel && (
          <span className="text-xs text-zinc-400">
            Generated {lastGeneratedLabel}
          </span>
        )}
      </div>

      <div className="px-4">
        <WikiSearch pages={wiki.pages} onSelectPage={onSelectPage} />
      </div>

      <div className="flex justify-end">
        <Button
          variant="ghost"
          size="sm"
          className="gap-1.5 text-xs text-zinc-500 hover:text-zinc-900"
          onClick={onRegenerate}
          disabled={isRegenerating}
        >
          <RefreshCw
            className={cn('h-3.5 w-3.5', isRegenerating && 'animate-spin')}
          />
          Regenerate
        </Button>
      </div>
    </div>
  );
};

export const MatterWiki = ({ matterId }: MatterWikiProps) => {
  const scrollViewRef = useRef<WikiScrollViewHandle>(null);
  const [activePath, setActivePath] = useState('/overview');
  const [selectedCitation, setSelectedCitation] = useState<WikiCitation | null>(
    null,
  );

  const [generateWiki, { isLoading: isGenerateLoading }] =
    useGenerateWikiMutation();

  const lastStatusRef = useRef<string | undefined>(undefined);

  const {
    data: currentWiki,
    isLoading,
    refetch,
  } = useGetWikiQuery(
    { matterId },
    {
      pollingInterval:
        lastStatusRef.current === 'in_progress' ? POLL_INTERVAL_MS : undefined,
    },
  );

  lastStatusRef.current = currentWiki?.generation_status;

  const activeCitationKey = useMemo(
    () => (selectedCitation ? getCitationKey(selectedCitation) : null),
    [selectedCitation],
  );

  const handleSelectPage = useCallback((path: string) => {
    setActivePath(path);
    scrollViewRef.current?.scrollToPath(path);
  }, []);

  const handleActivePathChange = useCallback((path: string) => {
    setActivePath(path);
  }, []);

  const handleCitationSelect = useCallback((citation: WikiCitation) => {
    setSelectedCitation(citation);
  }, []);

  const handleCloseCitationDrawer = useCallback(() => {
    setSelectedCitation(null);
  }, []);

  const handleGenerate = useCallback(async () => {
    await generateWiki({ matterId }).unwrap();
    refetch();
  }, [generateWiki, matterId, refetch]);

  const isRegenerating =
    isGenerateLoading || currentWiki?.generation_status === 'in_progress';

  if (isLoading) {
    return (
      <div className="h-full transition-opacity duration-300">
        <WikiLoadingSkeleton />
      </div>
    );
  }

  if (
    !currentWiki ||
    (currentWiki.generation_status === 'pending' &&
      currentWiki.pages.length === 0)
  ) {
    return (
      <div className="flex h-full items-center justify-center bg-zinc-50/50 transition-opacity duration-300">
        <WikiEmptyState
          onGenerate={handleGenerate}
          isGenerating={isGenerateLoading}
        />
      </div>
    );
  }

  if (
    currentWiki.generation_status === 'in_progress' &&
    currentWiki.pages.length === 0
  ) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 bg-zinc-50/50 transition-opacity duration-300">
        <div className="flex flex-col items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-zinc-100">
            <Loader2 className="h-5 w-5 animate-spin text-zinc-400" />
          </div>
          <div className="text-center">
            <p className="text-sm font-medium text-zinc-700">
              Generating your wiki
            </p>
            <p className="mt-1 text-xs text-zinc-400">
              This usually takes a minute or two
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (currentWiki.generation_status === 'failed') {
    return (
      <div
        data-wiki-container
        className="flex h-full flex-col bg-zinc-50/50 transition-opacity duration-300"
      >
        <div className="px-4 pt-4">
          <WikiGenerationBanner status="failed" onRetry={handleGenerate} />
        </div>

        {currentWiki.pages.length > 0 && (
          <>
            <WikiHeader
              wiki={currentWiki}
              isRegenerating={isRegenerating}
              onRegenerate={handleGenerate}
              onSelectPage={handleSelectPage}
            />
            <div className="flex min-h-0 flex-1">
              <div className="w-[240px] shrink-0 overflow-y-auto border-r border-zinc-100">
                <WikiSidebar
                  tree={currentWiki.tree}
                  activePath={activePath}
                  onSelectPage={handleSelectPage}
                />
              </div>
              <div className="flex-1 min-w-0">
                <WikiScrollView
                  ref={scrollViewRef}
                  matterId={matterId}
                  pages={currentWiki.pages}
                  onActivePathChange={handleActivePathChange}
                  onCitationSelect={handleCitationSelect}
                  activeCitationKey={activeCitationKey}
                />
              </div>
              <WikiCitationDrawer
                matterId={matterId}
                citation={selectedCitation}
                onClose={handleCloseCitationDrawer}
              />
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div
      data-wiki-container
      className="flex h-full flex-col bg-zinc-50/50 transition-opacity duration-300"
    >
      {currentWiki.generation_status === 'in_progress' && (
        <div className="px-4 pt-4">
          <WikiGenerationBanner status="in_progress" />
        </div>
      )}

      <WikiHeader
        wiki={currentWiki}
        isRegenerating={isRegenerating}
        onRegenerate={handleGenerate}
        onSelectPage={handleSelectPage}
      />

      <div className="flex min-h-0 flex-1">
        <div className="w-[240px] shrink-0 overflow-y-auto border-r border-zinc-100">
          <WikiSidebar
            tree={currentWiki.tree}
            activePath={activePath}
            onSelectPage={handleSelectPage}
          />
        </div>

        <div className="flex-1 min-w-0">
          <WikiScrollView
            ref={scrollViewRef}
            matterId={matterId}
            pages={currentWiki.pages}
            onActivePathChange={handleActivePathChange}
            onCitationSelect={handleCitationSelect}
            activeCitationKey={activeCitationKey}
          />
        </div>

        <WikiCitationDrawer
          matterId={matterId}
          citation={selectedCitation}
          onClose={handleCloseCitationDrawer}
        />
      </div>
    </div>
  );
};
